import React, { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DataTable } from "@/components/ui/data-table";
import { EnhancedPaginationControl } from "@/components/common/EnhancedPaginationControl";
import { MerchantReportTableSkeleton } from "@/components/reports/MerchantReportTableSkeleton";
import { MerchantReportFilters } from "@/components/reports/MerchantReportFilters";
import { MerchantApi } from "@/api/merchant.api";
import { ProgramApi } from "@/api/program.api";
import Transactions from "@/pages/Transactions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";
import { useUserInfo } from "@/hooks/use-user-info";
import { isProgramAdmin } from "@/utils/role.utils";

interface MerchantReportData {
  merchantName: string;
  numberOfTransactions: number;
  businessAmount: number;
  currencyCode: string;
  pointsAccrued: number;
  lastTransactionDate: string;
}

interface Program {
  programCode: string;
  programName: string;
}

export default function ReportsInsights() {
  const { userInfo } = useUserInfo();
  const [activeTab, setActiveTab] = useState("transactions");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isLoading, setIsLoading] = useState(false);
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>(
    undefined
  );
  const [selectedProgram, setSelectedProgram] = useState("");
  const [transactionProgramCode, setTransactionProgramCode] = useState("");
  const [programs, setPrograms] = useState<Program[]>([]);
  const [merchantData, setMerchantData] = useState<MerchantReportData[]>([]);
  const [totalMerchants, setTotalMerchants] = useState(0);

  // Fetch programs on component mount
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const response = await ProgramApi.getProgramsFromDB({
          pageNumber: 0,
          pageSize: 100,
          sortOrder: "asc",
          sortBy: "programName",
          programCode: null,
          programName: null,
          programType: "EARN",
        });
        const programsData = response.data?.programVOs || [];
        setPrograms(
          programsData.map((program: any) => ({
            programCode: program.programCode,
            programName: program.programName,
          }))
        );
      } catch (error) {
        console.error("Error fetching programs:", error);
        setPrograms([]);
      }
    };
    fetchPrograms();
  }, []);

  // Default first program for transactions tab
  useEffect(() => {
    if (programs.length === 0 || transactionProgramCode) return;
    const firstProgramCode = programs[0]?.programCode;
    if (firstProgramCode) {
      setTransactionProgramCode(firstProgramCode);
    }
  }, [programs, transactionProgramCode]);

  // Program admin: default to first program when programs load (merchants tab)
  useEffect(() => {
    if (
      !isProgramAdmin(userInfo?.roleCode) ||
      programs.length === 0 ||
      selectedProgram
    ) {
      return;
    }
    const firstProgramCode = programs[0].programCode;
    if (firstProgramCode) {
      handleProgramChange(firstProgramCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programs, userInfo?.roleCode]);

  // Refetch data when pagination changes
  useEffect(() => {
    if (selectedProgram) {
      fetchMerchantAnalytics(selectedProgram, customDateRange);
    }
  }, [currentPage, rowsPerPage]);

  // Dummy data for merchants report
  const dummyMerchantData: MerchantReportData[] = [
    {
      merchantName: "Amazon",
      numberOfTransactions: 1250,
      businessAmount: 45000.0,
      currencyCode: "USD",
      pointsAccrued: 2250,
      lastTransactionDate: "2024-01-15T10:30:00Z",
    },
    {
      merchantName: "Walmart",
      numberOfTransactions: 890,
      businessAmount: 32000.0,
      currencyCode: "USD",
      pointsAccrued: 1600,
      lastTransactionDate: "2024-01-14T15:45:00Z",
    },
    {
      merchantName: "Target",
      numberOfTransactions: 567,
      businessAmount: 18500.0,
      currencyCode: "USD",
      pointsAccrued: 925,
      lastTransactionDate: "2024-01-13T09:20:00Z",
    },
    {
      merchantName: "Best Buy",
      numberOfTransactions: 423,
      businessAmount: 15600.0,
      currencyCode: "USD",
      pointsAccrued: 780,
      lastTransactionDate: "2024-01-12T14:15:00Z",
    },
    {
      merchantName: "Home Depot",
      numberOfTransactions: 298,
      businessAmount: 8900.0,
      currencyCode: "USD",
      pointsAccrued: 445,
      lastTransactionDate: "2024-01-11T11:30:00Z",
    },
    {
      merchantName: "Starbucks",
      numberOfTransactions: 1567,
      businessAmount: 7800.0,
      currencyCode: "USD",
      pointsAccrued: 390,
      lastTransactionDate: "2024-01-15T08:45:00Z",
    },
    {
      merchantName: "McDonald's",
      numberOfTransactions: 2341,
      businessAmount: 12300.0,
      currencyCode: "USD",
      pointsAccrued: 615,
      lastTransactionDate: "2024-01-15T12:20:00Z",
    },
    {
      merchantName: "Nike",
      numberOfTransactions: 234,
      businessAmount: 18900.0,
      currencyCode: "USD",
      pointsAccrued: 945,
      lastTransactionDate: "2024-01-10T16:30:00Z",
    },
  ];

  // Calculate pagination
  const totalPages = Math.ceil(totalMerchants / rowsPerPage);
  const currentData = merchantData;

  // Handle date range change
  const handleDateRangeChange = (range: DateRange | undefined) => {
    setCustomDateRange(range);
    // Call API when end date is selected (both dates are present) and program is selected
    if (range?.from && range?.to && selectedProgram) {
      fetchMerchantAnalytics(selectedProgram, range);
    }
  };

  // Handle program change
  const handleProgramChange = (programCode: string) => {
    setSelectedProgram(programCode);
    setCurrentPage(0);
    if (programCode) {
      fetchMerchantAnalytics(programCode);
    } else {
      setMerchantData([]);
      setTotalMerchants(0);
    }
  };

  // Fetch merchant analytics data
  const fetchMerchantAnalytics = async (
    programCode: string,
    dateRange?: DateRange | undefined
  ) => {
    try {
      setIsLoading(true);
      
      // Convert date range to milliseconds
      let startDateInMillis: number | undefined = undefined;
      let endDateInMillis: number | undefined = undefined;
      
      if (dateRange?.from && dateRange?.to) {
        // Set start date to beginning of day (00:00:00)
        const startDate = new Date(dateRange.from);
        startDate.setHours(0, 0, 0, 0);
        startDateInMillis = startDate.getTime();
        
        // Set end date to end of day (23:59:59.999)
        const endDate = new Date(dateRange.to);
        endDate.setHours(23, 59, 59, 999);
        endDateInMillis = endDate.getTime();
      }
      
      const response = await MerchantApi.getMerchantsAnalytics({
        sortOrder: "ASC",
        sortBy: "noOfTransactions",
        pageNumber: currentPage,
        pageSize: rowsPerPage,
        programCode: programCode,
        startDateInMillis: startDateInMillis,
        endDateInMillis: endDateInMillis,
      });

      const data = response.data?.merchants || [];
      const mappedData = data.map((merchant: any) => ({
        merchantName: merchant.merchantName || "",
        numberOfTransactions: merchant.noOfTransactions || 0,
        businessAmount: merchant.businessAmount || 0,
        currencyCode: merchant.currencyCode || "",
        pointsAccrued: merchant.pointsAccrued || 0,
        lastTransactionDate: merchant.lastTransactionDate || "",
      }));

      setMerchantData(mappedData);
      setTotalMerchants(response.data?.totalRecords || 0);
    } catch (error) {
      console.error("Error fetching merchant analytics:", error);
      setMerchantData([]);
      setTotalMerchants(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setCustomDateRange(undefined);
    setSelectedProgram("");
    setCurrentPage(0);
    setMerchantData([]);
    setTotalMerchants(0);
  };

  // Table columns for merchants
  const merchantColumns = [
    {
      header: "Merchant Name",
      accessorKey: "merchantName" as keyof MerchantReportData,
    },
    {
      header: "No. of Transactions",
      accessorKey: "numberOfTransactions" as keyof MerchantReportData,
      cell: (row: MerchantReportData) => (
        <div className="font-medium">
          {row.numberOfTransactions.toLocaleString()}
        </div>
      ),
    },
    {
      header: "Business Amount",
      accessorKey: "businessAmount" as keyof MerchantReportData,
      cell: (row: MerchantReportData) => (
        <div className="font-medium">
          {row.businessAmount.toLocaleString()} {row.currencyCode}
        </div>
      ),
    },
    {
      header: "Points Accrued",
      accessorKey: "pointsAccrued" as keyof MerchantReportData,
      cell: (row: MerchantReportData) => (
        <div className="font-medium">{row.pointsAccrued.toLocaleString()}</div>
      ),
    },
    {
      header: "Last Transaction Date",
      accessorKey: "lastTransactionDate" as keyof MerchantReportData,
      cell: (row: MerchantReportData) => (
        <div className="text-sm text-gray-600">
          {row.lastTransactionDate
            ? format(new Date(row.lastTransactionDate), "MMM dd, yyyy HH:mm")
            : "N/A"}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
        <p className="text-sm text-gray-600 mt-1">
          Explore comprehensive reports and actionable insights to monitor
          performance and trends.
        </p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <TabsList>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="merchants">Merchants</TabsTrigger>
          </TabsList>
          {activeTab === "transactions" && (
            <div className="w-full sm:w-64">
              <Select
                value={transactionProgramCode || ""}
                onValueChange={setTransactionProgramCode}
                disabled={programs.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Program" />
                </SelectTrigger>
                <SelectContent>
                  {programs.map((program) => (
                    <SelectItem
                      key={program.programCode}
                      value={program.programCode}
                    >
                      {program.programName || program.programCode}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <TabsContent value="merchants" className="space-y-4">
          {/* Merchant Report Filters */}
          <MerchantReportFilters
            customDateRange={customDateRange}
            onDateRangeChange={handleDateRangeChange}
            onReset={handleResetFilters}
            selectedProgram={selectedProgram}
            onProgramChange={handleProgramChange}
            programs={programs}
          />

          {!selectedProgram ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Select a Program to View Merchant Report
              </h3>
              <p className="text-gray-500 max-w-md">
                Please select a program from the dropdown above to view merchant
                performance report and insights.
              </p>
            </div>
          ) : (
            <DataTable
              title="Merchant Performance Report"
              data={isLoading ? [] : currentData}
              columns={merchantColumns}
              // Match row density with Programs/Invoices tables
              rowClassName="hover:bg-gray-50 border-b border-gray-100"
              cellClassName="py-4 text-gray-700"
              headerCellClassName="text-gray-600 font-medium py-4"
              customTableBody={
                isLoading ? (
                  <MerchantReportTableSkeleton rowCount={rowsPerPage} />
                ) : undefined
              }
            />
          )}

          {/* Pagination Controls - Only show when program is selected */}
          {selectedProgram && (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">Rows per page:</span>
                <Select
                  value={rowsPerPage.toString()}
                  onValueChange={(value) => {
                    setRowsPerPage(Number(value));
                    setCurrentPage(0);
                  }}
                >
                  <SelectTrigger className="w-[80px]">
                    <SelectValue placeholder={rowsPerPage.toString()} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <EnhancedPaginationControl
                totalPages={totalPages}
                currentPage={currentPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
              <div className="w-[160px]" /> {/* Spacer to balance layout */}
            </div>
          )}
        </TabsContent>
        <TabsContent value="transactions" className="space-y-4">
          <Transactions
            showHeader={false}
            hideProgramDropdown
            programCode={transactionProgramCode}
            onProgramChange={setTransactionProgramCode}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
